import { Link } from "react-router-dom";
import { ArrowRight, Phone, Mail, Building2, Receipt } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import Reveal from "@/components/Reveal";
import TravelAgentRegistrationForm from "@/components/TravelAgentRegistrationForm";
import Seo from "@/components/Seo";
import { DEPARTMENTS } from "@/lib/data";

const HERO = "https://images.unsplash.com/photo-1521295121783-8a321d551ad2?auto=format&fit=crop&w=1800&q=80";

export default function TravelAgentPage() {
  const sales = DEPARTMENTS.find((d) => d.id === "agency-sales");
  const ticketing = DEPARTMENTS.find((d) => d.id === "ticketing");
  const refunds = DEPARTMENTS.find((d) => d.id === "refunds");

  return (
    <div data-testid="travel-agent-page">
      <Seo
        title="Travel Agent Centre — UK Trade Support | Simba Travel"
        description="UK travel agent hub for Air Zimbabwe: agency registration, dedicated ticketing and refund desks, downloadable documents, sales bulletins and direct trade contacts."
      />
      <section className="relative h-[60vh] min-h-[420px] -mt-24 flex items-end overflow-hidden">
        <img src={HERO} alt="Travel trade" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-midnight to-midnight/30" />
        <div className="relative container-edge pb-14 text-linen">
          <div className="overline text-ochre mb-4">Trade · UK & Ireland</div>
          <h1 className="editorial-h1 max-w-3xl">Travel Agent Centre.</h1>
        </div>
      </section>

      {/* Intro */}
      <section className="section-pad bg-linen">
        <div className="container-edge max-w-3xl">
          <p className="text-lg leading-relaxed text-ink/80 font-light">
            A single dedicated hub for UK travel agents — agency registration, dedicated ticketing &amp; refund desks, downloadable documents and the latest sales bulletins from Simba Travel and our airline partners.
          </p>
        </div>
      </section>

      {/* Quick contacts */}
      <section className="bg-sand py-14">
        <div className="container-edge grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
          {[sales, ticketing, refunds].map((d) => (
            <div key={d.id} className="bg-linen p-8" data-testid={`trade-contact-${d.id}`}>
              <div className="overline text-terracotta">{d.name}</div>
              <a href={`tel:${d.phone}`} className="block mt-3 font-serif text-2xl hover:text-terracotta transition-colors">
                {d.phoneIntl}
              </a>
              <a href={`mailto:${d.email}`} className="block mt-1 text-xs text-muted2 hover:text-terracotta">
                {d.email}
              </a>
              <p className="text-xs text-muted2 mt-3 leading-relaxed">{d.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Registration */}
      <section className="section-pad bg-linen" data-testid="agent-centre-register">
        <div className="container-edge">
          <Reveal className="max-w-3xl mb-10">
            <div className="overline text-terracotta mb-4">Step 1</div>
            <h2 className="editorial-h2">Register your agency.</h2>
            <p className="mt-6 text-muted2 leading-relaxed">
              Join the Simba Travel network to book Air Zimbabwe for your customers. Access
              dedicated trade fares, priority ticketing and a UK-based agency desk open seven
              days a week.
            </p>
            <ul className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              {[
                "Dedicated agency sales contact",
                "Fares loaded across all major GDS",
                "Direct ticketing & reissue support",
                "Refund applications via dedicated desk",
                "Familiarisation trip eligibility",
                "Trade newsletters & incentives",
              ].map((b) => (
                <li key={b} className="flex gap-3 items-start">
                  <span className="w-1.5 h-1.5 bg-terracotta rounded-full mt-2" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <div className="max-w-3xl border border-ink/10 bg-sand p-6 md:p-10 shadow-[0_20px_60px_-30px_rgba(19,26,34,0.2)]">
            <TravelAgentRegistrationForm />
          </div>
        </div>
      </section>

      {/* Ticketing + Refund tile */}
      <section className="section-pad bg-sand">
        <div className="container-edge grid grid-cols-1 md:grid-cols-2 gap-px bg-ink/10 border border-ink/10">
          <div className="bg-linen p-10 md:p-14">
            <Receipt className="w-7 h-7 stroke-1 text-terracotta" />
            <h3 className="editorial-h3 mt-6">Ticketing support</h3>
            <p className="mt-4 text-muted2 leading-relaxed">
              Same-day ticket issuance, reissues, voids, EMD ancillaries and complex multi-stop itineraries — handled by experienced GDS-trained ticketers.
            </p>
            <div className="mt-6 flex gap-3 flex-wrap text-sm">
              <a href={`tel:${ticketing.phone}`} className="btn-secondary"><Phone className="w-4 h-4" /> {ticketing.phoneIntl}</a>
              <a href={`mailto:${ticketing.email}`} className="text-sm self-center text-muted2 hover:text-terracotta"><Mail className="w-4 h-4 inline mr-1" />{ticketing.email}</a>
            </div>
          </div>
          <div className="bg-linen p-10 md:p-14">
            <Building2 className="w-7 h-7 stroke-1 text-terracotta" />
            <h3 className="editorial-h3 mt-6">Refund requests</h3>
            <p className="mt-4 text-muted2 leading-relaxed">
              Structured refund process aligned with airline conditions of carriage — refund applications acknowledged within 48 hours and processed via BSP-UK.
            </p>
            <Link to="/contact" className="btn-secondary mt-6" data-testid="refund-cta">
              Refund desk <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
